import { Form } from 'informed';
import styled, { createGlobalStyle, css } from 'styled-components';

import { Button } from '@nw/omega-design-system';
import { colors } from '@nw/shared/config';
import { Image } from '@nw/shared/ui';
import { maxDeviceWidth } from '@nw/shared/utils';

export const ApplicationFeeSectionContainer = styled.div<{
  $withCardShell?: boolean;
}>`
  box-sizing: border-box;
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;

  &.application-fee-section {
    gap: 12px;
  }

  ${({ $withCardShell }) =>
    $withCardShell &&
    css`
      border: 1px solid ${colors['light-blue-gray-300']};
      padding: 32px;
      border-radius: 16px;
      background-color: ${colors['base-white']};

      ${maxDeviceWidth(768)} {
        padding: 16px;
      }
    `}
`;

export const UserDetailsAndInfoCardContainer = styled.div`
  width: 100%;
`;

export const UserDetailsCardContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  width: inherit;
  padding: 16px 18px;
  border-radius: 8px;
  margin: 16px 0px;
  background-color: ${colors['light-amber-50']};

  ${maxDeviceWidth(1199)} {
    padding: 8px;
  }
`;

export const UserDetailsFieldsStack = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;

  ${maxDeviceWidth(767)} {
    gap: 8px;
  }
`;

export const UserDetailsFieldRow = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  width: 100%;
  min-width: 0;

  ${maxDeviceWidth(767)} {
    gap: 4px;
    align-items: flex-start;
  }
`;

export const UserDetailsTwoColumnRow = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 72px;
  width: 100%;
  min-width: 0;

  ${maxDeviceWidth(767)} {
    flex-direction: column;
    gap: 8px;
  }
`;

export const UserDetailsFieldGroup = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  min-width: 0;
  flex: 1;

  ${maxDeviceWidth(767)} {
    gap: 4px;
    align-items: flex-start;
  }
`;

export const UserInfo = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
`;

export const UserInfoLabel = styled.span`
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  text-align: left;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
  color: ${colors.slate};
  flex-shrink: 0;
  width: 145px;
  ${maxDeviceWidth(1199)} {
    font-size: 12px;
    line-height: 20px;
  }
  ${maxDeviceWidth(767)} {
    width: 100px;
  }
`;

export const CommonUserInfoLabel = styled.span`
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  text-align: left;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
  color: #475569;
  flex-shrink: 0;
  ${maxDeviceWidth(1199)} {
    font-size: 12px;
    line-height: 20px;
  }
`;

export const UserInfoValue = styled.span`
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  text-align: left;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
  color: ${colors['light-blue-gray-800']};
  word-break: break-word;
  min-width: 0;
  &.enrollment-highlight-value {
    font-weight: 500;
    color: ${colors['light-red-900']};
  }
  ${maxDeviceWidth(1199)} {
    font-size: 12px;
    line-height: 20px;
  }
  ${maxDeviceWidth(767)} {
    font-weight: 600;
    &.enrollment-highlight-value {
      font-weight: 600;
    }
  }
`;

export const CommonUserInfoValue = styled.span`
  font-family: Inter;
  font-size: 14px;
  font-weight: 600;
  line-height: 24px;
  text-align: left;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
  color: #334155;
  word-break: break-all;
  ${maxDeviceWidth(1199)} {
    font-size: 12px;
    line-height: 20px;
  }
`;

export const ApplicationFeeCardContainer = styled(Form)`
  width: 100%;
`;

export const ApplicationFeeFieldsContainer = styled.div`
  width: 100%;

  &.enrollment-fee-fields {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 12px 24px 24px;
    border: 1px solid ${colors['light-blue-gray-300']};
    border-radius: 16px;
    background-color: ${colors['base-white']};

    ${maxDeviceWidth(768)} {
      padding: 16px;
      gap: 12px;
    }
  }
`;

export const ApplicationFeeLabelContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  min-width: 0;
  margin-bottom: 18px;

  .enrollment-fee-fields & {
    margin-bottom: 0;
  }

  ${maxDeviceWidth(480)} {
    flex-wrap: wrap;
    row-gap: 8px;
  }
`;

export const ApplicationFeeLabel = styled.div`
  font-family: Inter;
  font-size: 18px;
  font-weight: 500;
  line-height: 24px;
  text-align: left;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
  color: #475569;
  margin-bottom: 0;
  flex: 1;
  min-width: 0;
  ${maxDeviceWidth(1199)} {
    font-size: 16px;
  }
`;

export const ApplicationFeeValue = styled.div`
  font-family: Inter;
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  text-align: right;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
  color: #334155;
  flex-shrink: 0;
  white-space: nowrap;
  ${maxDeviceWidth(1199)} {
    font-size: 20px;
    line-height: 28px;
  }
`;

export const DottedLineBreak = styled.div`
  height: 1px;
  width: 100%;
  margin-top: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid #cbd5e1;
`;

export const CouponCodeFieldAndApplyButtonContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: flex-start;
  margin-top: 12px;
  max-width: 480px !important;

  /* Match take-admission invite row: top-align row so error/helper text below the field
     does not push the Apply button down (flex-end was centering against label+input+error). */
  .enrollment-fee-fields & {
    margin-top: 12px;
    margin-bottom: 24px;
    align-items: flex-start;

    .application-fee-apply-button {
      margin-top: 24px;
    }

    ${maxDeviceWidth(480)} {
      .application-fee-apply-button {
        margin-top: 12px;
      }
    }
  }

  .application-fee-apply-button {
    box-shadow: none !important;
  }

  ${maxDeviceWidth(480)} {
    flex-direction: column;
  }

  .application-fee-coupon-code-field {
    margin-bottom: 0px !important;
    width: 100% !important;
  }

  &.take-admission-invite-code-container {
    max-width: 520px !important;
    align-items: flex-start;
    margin-bottom: 12px;

    .application-fee-apply-button {
      margin-top: 24px;
    }

    ${maxDeviceWidth(480)} {
      .application-fee-apply-button {
        margin-top: 12px;
      }
    }
  }
`;

export const ContinueToPayButton = styled(Button)`
  background-color: #16a34a !important;
  border-radius: 8px !important;
  padding: 12px;
  width: 100%;
  max-width: 100% !important;
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: center;

  svg {
    margin-left: 8px !important;
  }

  &:focus {
    outline: none !important;
    box-shadow: none !important;
  }
  ${maxDeviceWidth(420)} {
    align-self: center !important;
  }
`;

export const FooterActionButtonsContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  ${maxDeviceWidth(768)} {
    flex-direction: column;
    align-items: stretch;
    gap: 2px;
  }
`;

export const SharePaymentLinkButton = styled(Button)`
  border: none;
  background: transparent;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 0;
  color: ${colors['light-blue-gray-600']};
  font-family: Inter;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  width: 100%;
  white-space: nowrap;
  outline: none !important;
  border: none !important;
  box-shadow: none !important;

  .share-payment-link-icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  ${maxDeviceWidth(768)} {
    order: 2;
    width: 100%;
  }
`;

export const ShareIconPlaceholder = styled.span`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  margin-right: 8px;
`;

export const PaymentMethodsIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ErrorMessage = styled.div`
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  text-align: left;
  margin-bottom: 8px;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
  color: rgba(220, 38, 38);
`;

export const stateOfResidenceCss = css`
  margin-top: 12px;
  .informedInputLabelText::after {
    content: '*';
    color: red;
  }
`;

export const CouponCodeText = styled.div`
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  text-align: left;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
  color: #16a34a;
  margin-top: 16px;
  cursor: pointer;

  .enrollment-fee-fields & {
    margin-top: 0;
    color: ${colors['light-blue-gray-600']};
    font-weight: 400;
  }

  * {
    pointer-events: none;
  }
`;

export const CouponCodeLinkAccent = styled.span`
  color: ${colors['light-red-900']};
`;

export const SectionFooterContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  justify-content: center;
  align-items: center;

  ${maxDeviceWidth(768)} {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: #fff;
    padding: 10px 24px 18px;
    border-top: 1px solid #cbd5e1;
    z-index: 0;
  }
`;

export const StudentCountDescription = styled.p`
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  text-align: left;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
  color: #475569;

  @media (max-width: 768px) {
    font-size: 12px;
    line-height: 20px;
  }
`;

export const SectionFooterContainerGlobalStyle = createGlobalStyle`
  body .application-sections-container {
    @media (max-width: 768px) {
      padding: 16px 24px 176px;
    }
  }
`;

export const TakeAdmissionCompactField = styled.div`
  width: 100%;
  max-width: 420px;
`;

export const ApplicationFeeValueAfterDiscount = styled.span`
  margin-left: 8px;
  text-decoration: line-through;
  color: #94a3b8;
  font-weight: 400;
  font-size: 20px;
  line-height: 32px;

  @media (max-width: 768px) {
    font-size: 18px;
    line-height: 24px;
  }
`;

export const EnrollmentPartialPaymentCard = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
  padding: 16px;
  border: 1px solid ${colors['light-blue-gray-300']};
  border-radius: 16px;
  background-color: ${colors['base-white']};

  @media (min-width: 768px) {
    gap: 24px;
    padding: 24px;
    border-radius: 20px;
  }
`;

export const EnrollmentPartialPaymentCardBody = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;

  ${maxDeviceWidth(768)} {
    gap: 12px;
  }
`;

export const EnrollmentPartialPaymentHeader = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

export const EnrollmentPartialPaymentHeaderRow = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
`;

export const EnrollmentPartialPaymentTitle = styled.p`
  margin: 0;
  color: ${colors['light-blue-gray-900']};
  font-family: Inter;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;

  @media (min-width: 768px) {
    font-size: 20px;
    font-weight: 500;
    line-height: 32px;
  }
`;

export const EnrollmentTotalPaymentAmount = styled.p`
  margin: 0;
  color: ${colors['light-blue-gray-700']};
  font-family: Inter;
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (min-width: 768px) {
    font-size: 24px;
    line-height: 40px;
  }
`;

export const EnrollmentPartialPaymentTotalLabel = styled.span`
  margin-right: 4px;
  color: ${colors['light-blue-gray-500']};
  font-family: Inter;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0.22px;
  text-transform: uppercase;
  vertical-align: middle;
`;

export const StudentCountDescriptionContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 6px;
  margin-top: 8px;
`;

export const StudentCountImage = styled(Image)`
  width: 50px;
  height: 24px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 34px;
    height: 16px;
  }
`;

export const EnrollmentSuccessTitle = styled.span`
  font-family: Inter;
  font-weight: 600;
  font-size: 24px;
  line-height: 40px;
  text-align: center;
  color: ${colors['light-green-700']};

  ${maxDeviceWidth(767)} {
    font-size: 20px;
    line-height: 32px;
  }
`;
export const EnrollmentSuccessSubtitle = styled.span`
  display: block;
  font-family: Inter;
  font-weight: 400;
  font-size: 16px;
  line-height: 28px;
  text-align: center;
  color: ${colors['light-green-900']};

  ${maxDeviceWidth(767)} {
    font-size: 12px;
    line-height: 20px;
  }
`;

export const EnrollmentSuccessLayout = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 28px;
  width: 100%;

  ${maxDeviceWidth(767)} {
    gap: 16px;
  }
`;

export const EnrollmentSuccessPromotionsSection = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 28px;
  width: 100%;

  ${maxDeviceWidth(767)} {
    gap: 16px;
  }
`;

export const EnrollmentSuccessPageContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 24px;
  width: 100%;
  padding: 32px;
  border: 1px solid ${colors['light-blue-gray-300']};
  border-radius: 16px;
  background: linear-gradient(
    180deg,
    ${colors['light-green-50']} 0%,
    ${colors['base-white']} 32%
  );

  ${maxDeviceWidth(767)} {
    gap: 16px;
    padding: 16px;
    background: ${colors['base-white']};
  }
`;

export const EnrollmentSuccessHeroSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 100%;

  ${maxDeviceWidth(767)} {
    padding-bottom: 20px;
  }
`;

export const EnrollmentSuccessBodySection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 24px;
  width: 100%;
`;

export const EnrollmentSuccessPaymentHistorySection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

export const EnrollmentSuccessHeadingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

  .enrollment-success-icon {
    width: 40px !important;
    height: 40px !important;
    flex-shrink: 0;
  }

  ${maxDeviceWidth(767)} {
    gap: 12px;

    .enrollment-success-icon {
      width: 32px !important;
      height: 32px !important;
    }
  }
`;
